import { useEffect, useState } from "react";
import {
  actionsInterface,
  columnsInterface,
} from "../../../_metronic/partials/widgets";
import axios, { AxiosRequestHeaders } from "axios";
import { getAuth } from "../../modules/auth";
import { showErrorMessage } from "../../../_metronic/helpers/MessagesHelper";

import clsx from "clsx";
import { useStyleContext } from "../../../_metronic/helpers/StyleContext";
import { BadgeStatus } from "../../../_metronic/partials/badges/statusBadge";
import { KTSVG } from "../../../_metronic/helpers";
import { Link } from "react-router-dom";
import DateFormat from "../../../_metronic/partials/format/DateFormat";
import ColorIndicator from "../../../_metronic/partials/color-indicators/ColorIndicator";

type ListByUserIdProps = {
  _user_id: number;
  actions: actionsInterface[];
  columns: columnsInterface[];
  operation: string;
};

const ListByUserId: React.FC<ListByUserIdProps> = ({
  _user_id,
  actions,
  columns,
  operation,
}) => {
  const { styleState } = useStyleContext();

  const [data, setData] = useState([]);
  const module = `users/${_user_id ?? null}/${operation}`;
  const moduleData = operation;
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiToken = getAuth();
  const token = apiToken?.api_token;
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    amount: 5,
    itemsFound: 0,
    total: 0,
  });

  const [filters] = useState({
    status: "all",
    search: "",
  });

  const loadList = async (module: string, headers: AxiosRequestHeaders) => {
    return await axios
      .get(`${apiUrl}/${module}`, {
        headers,
        params: {
          amount: pagination.amount,
          page: pagination.page,
          status: filters.status,
          ...(!!filters?.search ? { search: filters.search } : {}),
        },
      })
      .then((response) => {
        setData(response.data?.[`${moduleData}`]);
        setIsLoading(false);
        setPagination({
          page: response.data?.page,
          amount: response.data?.amount,
          itemsFound: response.data?.[`${moduleData}`]?.length,
          total: response.data?.total,
        });
      })
      .catch((error) => {
        setIsLoading(false);
        showErrorMessage(
          error?.response?.data?.error?.statusCode,
          error?.response?.data?.error?.message
        );
      });
  };

  useEffect(() => {
    const findHeaders = {
      Authorization: `Bearer ${token}`,
      operation: `${operation ?? module}/find`,
    };

    loadList(module, findHeaders);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, module, filters]);

  return (
    <>
      {!isLoading ? (
        <table className="table table-responsive align-middle text-center">
          {data.length <= 0 ? null : (
            <thead>
              <tr
                className={clsx(" fw-bold", {
                  "text-muted bg-light": !styleState.secondary_text_color,
                })}
                style={{
                  backgroundColor: styleState?.secondary_text_color,
                  color: styleState?.secondary_color,
                }}
              >
                {columns?.map((column, columnIndex) => (
                  <th key={columnIndex} className={column.className}>
                    {column?.description}
                  </th>
                ))}
              </tr>
            </thead>
          )}

          <tbody>
            {data.length <= 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  <span className="text-hover-primary fs-6 m-0 text-center p-1">
                    Não foi encrontrado nenhum registro.
                  </span>
                </td>
              </tr>
            ) : (
              data?.map((item, itemIndex) => (
                <tr key={itemIndex}>
                  {columns?.map((column, columnIndex) => (
                    <td
                      key={column.reference || columnIndex}
                      className={
                        column.reference === "actions"
                          ? "row justify-content-center"
                          : column.reference === "status" &&
                            column.type === "status-text"
                          ? "text-center"
                          : column.reference === "_id"
                          ? "ps-4"
                          : ""
                      }
                    >
                      {column.reference !== "status" &&
                        column.type !== "date" &&
                        column.type !== "color" && (
                          <span
                            className={clsx(
                              `fw-bold ${
                                column?.test_compliance &&
                                (!item["compliance"] || item["overdue"])
                                  ? "text-danger text-hover-danger"
                                  : "text-hover-primary"
                              } mb-1 fs-6`,
                              !column?.test_compliance &&
                                item["compliance"] && {
                                  "text-dark": !styleState.secondary_text_color,
                                }
                            )}
                            style={{
                              color: styleState?.secondary_text_color,
                            }}
                          >
                            {item[column.reference]}
                          </span>
                        )}

                      {column.type === "color" && (
                        <ColorIndicator
                          hexColor={item[column.reference]}
                          key={`color-indicator-${columnIndex}`}
                        />
                      )}
                      {column.type === "date" && (
                        <span
                          className={clsx(
                            "fw-bold text-hover-primary mb-1 fs-6",
                            {
                              "text-dark": !styleState.secondary_text_color,
                            }
                          )}
                          style={{
                            color: styleState?.secondary_text_color,
                          }}
                        >
                          <DateFormat
                            date={item[column.reference]}
                          ></DateFormat>
                        </span>
                      )}
                      {column.reference === "status" &&
                        column.type === "contract-status" && (
                          <BadgeStatus
                            description={item["status"]?.["description"]}
                          />
                        )}

                      {column.reference === "actions" &&
                        actions?.map((action: any, actionIndex) => { // 🟢 Adicionado 'any' para evitar erro de tipo na propriedade action
                          const actionKey = `${itemIndex}-${actionIndex}`;
                          return (
                            <span key={actionKey} className="d-flex justify-content-center"> {/* 🟢 Trocado React.Fragment por tag vazia ou span */}
                              {action.type === "link" && (
                                <div className="col-6 col-sm-3 mb-2 p-0 d-flex justify-content-center w-50px">
                                  <Link
                                    to={`/users/${item["_id"]}${action.url}`}
                                    title={action?.title}
                                    className={clsx("btn btn-icon btn-sm me-1", {
                                      "btn-bg-light btn-active-color-primary": !styleState.secondary_text_color,
                                      "text-hover-primary bg-hover-light": styleState.secondary_text_color,
                                    })}
                                    style={{ backgroundColor: styleState?.secondary_text_color }}
                                  >
                                    <KTSVG path={action?.icon} className="svg-icon-3" color={styleState?.secondary_color} />
                                  </Link>
                                </div>
                              )}

                              {(action.type === "button" || action.type === "action") && (
                                <div className="col-6 col-sm-3 mb-2 p-0 d-flex justify-content-center w-50px">
                                  <button
                                    type="button"
                                    title={action?.title}
                                    onClick={() => {
                                      if (action.action) action.action(item);
                                      else if (action.onClick) action.onClick(item);
                                    }}
                                    className={clsx("btn btn-icon btn-sm me-1", {
                                      "btn-bg-light btn-active-color-primary": !styleState.secondary_text_color,
                                      "text-hover-primary bg-hover-light": styleState.secondary_text_color,
                                    })}
                                    style={{ backgroundColor: styleState?.secondary_text_color }}
                                  >
                                    {action?.icon?.startsWith("fa-") ? (
                                      <i className={action?.icon} style={{ color: styleState?.secondary_color }}></i>
                                    ) : (
                                      <KTSVG path={action?.icon} className="svg-icon-3" color={styleState?.secondary_color} />
                                    )}
                                  </button>
                                </div>
                              )}
                            </span>
                          );
                        })}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      ) : (
        <span className="spinner-border spinner-border-xl align-middle ms-2"></span>
      )}
    </>
  );
};

export default ListByUserId;
